/* eslint-disable max-statements */

import { Button, Flex, ScrollArea, Stack, Title } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { cloneDeep } from 'lodash';
import { useEffect } from 'react';

import { Customer } from '@e-origin/shared';

import { DocumentChangeProps, getDeclarationDocumentById, getDeclarationDocumentPos, LeavePageNotification, StickyContainer, TextInput } from '@components';
import { EditCustomerType } from '@enums';
import { MutateDeclarationDocumentOperation } from '@hooks/declaration';
import { GuaranteesDocumentList } from './GuaranteesDocumentList';
import { useCustomerViewImportGuaranteesFormInitializer } from './useCustomerViewImportGuaranteesFormInitializer';

interface CustomerViewImportGuaranteesProps {
  handleSubmitCustomer(type: EditCustomerType, childForm: UseFormReturnType<any>): void;
  customerDetails: Customer;
  language: string;
  isCreate: boolean;
  isButtonDisabled: boolean;
}

export const CustomerViewImportGuarantees: React.FC<CustomerViewImportGuaranteesProps> = ({
  handleSubmitCustomer,
  isButtonDisabled,
  language,
  customerDetails,
  isCreate,
}) => {
  const { form, buildFormValues } = useCustomerViewImportGuaranteesFormInitializer({ customerDetails });
  const { documents, personProvidingGuarantee } = form.values;

  const handleDocumentChange = (doc: DocumentChangeProps) => {
    const refreshedDocuments = cloneDeep(documents);

    switch (doc.operation) {
      case MutateDeclarationDocumentOperation.CREATE: {
        refreshedDocuments[doc.type] = [...(refreshedDocuments[doc.type] || []), doc.data];
        break;
      }
      case MutateDeclarationDocumentOperation.UPDATE: {
        const documentToUpdate = getDeclarationDocumentById(doc.id, refreshedDocuments[doc.type]);
        const documentPos = refreshedDocuments[doc.type].findIndex((d) => d === documentToUpdate);
        refreshedDocuments[doc.type][documentPos] = doc.data;
        break;
      }
      case MutateDeclarationDocumentOperation.DELETE: {
        const index = getDeclarationDocumentPos(doc.data, doc.type, refreshedDocuments[doc.type]);

        if (index !== -1) {
          refreshedDocuments[doc.type].splice(index, 1);
        }
        break;
      }
      default:
        break;
    }

    form.setFieldValue('documents', refreshedDocuments);
  };

  useEffect(() => {
    if (customerDetails) {
      form.setValues(buildFormValues(customerDetails));
      form.resetDirty({} as any);
    }
  }, [customerDetails]);

  return (
    <>
      <LeavePageNotification hasUnsavedChanges={form.isDirty()} />

      <ScrollArea h="100%" offsetScrollbars="y">
        <Stack mx="xl">
          <Stack mb="xl">
            <Title order={5} fw={600}>
              Person providing a guarantee
            </Title>
            <Flex mb={15}>
              <TextInput
                {...form.getInputProps('personProvidingGuarantee.identificationNumber')}
                label="Identification number"
                value={personProvidingGuarantee.identificationNumber}
              />
            </Flex>
          </Stack>

          <Stack mb="xl">
            <Title order={5} fw={600}>
              Manage Guarantees
            </Title>
            <GuaranteesDocumentList documents={documents as any} onDocumentChange={handleDocumentChange} language={language} />
          </Stack>
        </Stack>

        <StickyContainer>
          <Button
            disabled={isButtonDisabled || !form.isValid()}
            onClick={() => handleSubmitCustomer(EditCustomerType.DOCUMENTS, form)}
            type="submit"
            variant="filled"
            size="lg"
          >
            {isCreate ? 'Create' : 'Save'}
          </Button>
        </StickyContainer>
      </ScrollArea>
    </>
  );
};
